import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { SnackBarStoreAction } from "Store/SnackBar";
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SnackBar from "components/SnackBar/SnackBar";
import PropTypes from "prop-types";
import { styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import classes from "./ACCV2.module.css";
import { getAccData, saveAccData } from "services/userServices";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { bulkEditStoreAction } from "../../../Store/BulkEdit";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import BackdropComponent from "../../Spinner/Spinner";
import PowerSharpIcon from "@mui/icons-material/PowerSharp";
import LastUpdateTooltip from "../Helpers/LastUpdateTooltip";
import moment from "moment";
import applicationUsageLog from "../../../utils/applicationUsagesLog";
import { MINIMUM_DATE } from "../../../config/CONSTANTS";

const getFilteredData = (data, property, value) =>
  data.filter((itm) => itm[property] === value);

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  marginTop: "-10px",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));
const CustomOutlinedButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  marginTop: "-10px",
  "&:hover": {
    borderColor: "#3299FF",
    backgroundColor: "#E0F2FF",
  },
}));

const formatDate = (date) =>
  moment(date ? new Date(date) : new Date("yyyy-MM-ddThh:mm")).format(
    "YYYY-MM-DD",
  );

const formatTime = (time) => moment(time || "00:00", "hh:mm A").format("HH:mm");

const ACCV2 = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(["ACC"]);
  const [loading, setLoading] = useState(false);
  const [accData, setAccData] = useState([]);
  const [order, setOrder] = useState("ASC");
  const [sortFlag, setSortFlag] = useState({});
  const [allData, setAllData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [globalDate, setGlobalDate] = useState("");
  const classTraineeId = useSelector((state) => state.bulkEdit);
  const initialRender = useRef(true);
  const [agentComments, setAgentComments] = useState("");
  const { classRequestId, classRosterId } = classTraineeId;

  const [totalActiveCount, setTotalActiveCount] = useState({
    total: 0,
    active: 0,
  });
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        await fetchData();

        if (!cancelled) {
          applicationUsageLog(
            localStorage.getItem("User"),
            localStorage.getItem("EmailId"),
            "ASTT",
            "ACC",
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to initialize:", error);
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const refreshData = async () => {
      if (props.resetStatus === true) {
        try {
          await fetchData();
        } catch (error) {
          if (!cancelled) {
            console.error("Failed to refresh data:", error);
          }
        }
      }
    };

    refreshData();

    return () => {
      cancelled = true;
    };
  }, [props.resetStatus]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      setLoading(true);

      const timeout = setTimeout(() => {
        filtering(props.filterStatus, filterData);
        setLoading(false);
      }, 1000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [props.filterStatus]);

  const fetchData = async () => {
    setLoading(true);
    let obj = {
      classTraineeIdList: classTraineeId.traineeDetails,
    };

    const response = await getAccData(obj);

    if (response.message !== "Success") {
      handleFetchError();
      return;
    }

    const data = transformResponseData(response.data);
    updateStateWithFetchedData(data);

    if (props.resetStatus === true) {
      resetAfterFetch(data);
    }

    if (response.data) {
      filtering(props.filterStatus, data);
    }

    setLoading(false);
  };

  const transformResponseData = (data) => {
    return data.map((itm, i) => {
      return {
        ...itm,
        date: itm.ACCDate
          ? formatDate(itm.ACCDate) + "T" + formatTime(itm.ACCTime)
          : "YYYY-MM-DDTHH:mm",
        SRWdate: itm.ACCSRWDisclaimerSignedDate
          ? moment(new Date(itm.ACCSRWDisclaimerSignedDate)).format(
              "YYYY-MM-DD",
            )
          : "",
        SRWtime: itm.ACCSRWDisclaimerSignedDate
          ? moment(new Date(itm.ACCSRWDisclaimerSignedDate)).format("hh:mm A")
          : "",
      };
    });
  };

  const updateStateWithFetchedData = (data) => {
    setAllData(data);
    setAccData(data);
    setFilterData(data);
    dispatch(bulkEditStoreAction.setAccData(data));
    dispatch(bulkEditStoreAction.setAccAllData(data));
  };

  const resetAfterFetch = (data) => {
    setAccData(data);
    setAgentComments("");
    props.onChange();
    setGlobalDate("");
  };

  const handleFetchError = () => {
    setSnack({
      open: true,
      message: "Failed to load data!",
      severity: "error",
    });
    setLoading(false);
  };

  const filtering = (filter, data) => {
    let filterData = [];

    if (filter === "ALL") {
      filterData = data;
    } else {
      filterData = data.filter((item) => item.TraineeStatus === filter);
    }
    setAccData(filterData);
    initialCheck(filterData);
    props.closeLoader();
  };

  const initialCheck = (data) => {
    let activeCount = getFilteredData(data, "TraineeStatus", "Active");
    setTotalActiveCount({
      total: data.length,
      active: activeCount.length,
    });
  };

  const shouldUpdateKey = (item, index) => {
    return (
      (props.filterStatus === "ALL" &&
        item.OracleId === allData[index].OracleId) ||
      (props.filterStatus !== "ALL" &&
        item.TraineeStatus === props.filterStatus)
    );
  };

  const findInitialData = (oracleId) => {
    return allData.find((item) => item.OracleId === oracleId);
  };

  const sorting = (col) => {
    const sorted = [...accData].sort((a, b) => {
      const valA = a[col]?.toLowerCase() || "";
      const valB = b[col]?.toLowerCase() || "";

      if (order === "ASC") {
        return valA > valB ? 1 : -1;
      } else {
        return valA < valB ? 1 : -1;
      }
    });

    setAccData(sorted);
    setOrder(order === "ASC" ? "DSC" : "ASC");
    setSortFlag({ [col]: order });
  };

  const handleAll = (eventValue, key) => {
    if (key === "date") {
      setGlobalDate(eventValue);
    } else {
      setAccData({ [key]: eventValue });
    }

    const isResetValue = eventValue === "" || eventValue === " ";
    const data = isResetValue
      ? buildResetData(key)
      : buildUpdateData(eventValue, key);

    const updateAccData = isResetValue
      ? resetAccDataValues(key)
      : applyValueToAccData(eventValue, key);

    setAccData(updateAccData);
    setFilterData(data);
    dispatch(bulkEditStoreAction.setAccData(updateAccData));
  };

  const buildResetData = (key) => {
    return filterData
      .map((item, i) => {
        if (shouldUpdateKey(item, i)) {
          return {
            ...item,
            [key]: allData[i][key],
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const buildUpdateData = (eventValue, key) => {
    return filterData.map((item) => {
      const shouldUpdate =
        props.filterStatus === "ALL" ||
        props.filterStatus === item.TraineeStatus;

      return shouldUpdate ? { ...item, [key]: eventValue } : { ...item };
    });
  };

  const resetAccDataValues = (key) => {
    return accData.map((item) => {
      const initialData = findInitialData(item.OracleId);
      return {
        ...item,
        [key]: initialData[key],
      };
    });
  };

  const applyValueToAccData = (eventValue, key) => {
    return accData.map((item) => ({
      ...item,
      [key]: eventValue,
    }));
  };

  const renderTextCell = (nameWithId, traineeStatus, isInactive) => (
      <Grid item xs={0.8} align="center">
        <Typography
          sx={{
            fontFamily: "Urbanist",
            fontSize: "13px",
            color: "#3F5765",
            textDecoration: isInactive ? "line-through" : "none",
          }}
        >
          {nameWithId}
        </Typography>
        {isInactive && <p className={classes.blueColor}>{traineeStatus}</p>}
      </Grid>
    );

  const handleIndividualChange = (event, row, key) => {
    let data = filterData.map((item) => {
      if (
        item.OracleId === row.OracleId &&
        item.TraineeStatus === row.TraineeStatus
      ) {
        return {
          ...item,
          [key]: event,
        };
      } else {
        return item;
      }
    });

    let trainee = accData.map((item) => {
      if (
        item.OracleId === row.OracleId &&
        item.TraineeStatus === row.TraineeStatus
      ) {
        return {
          ...item,
          [key]: event === "" ? null : event,
        };
      } else {
        return item;
      }
    });
    setAccData(trainee);
    setFilterData(data);
    dispatch(bulkEditStoreAction.setAccData(trainee));
  };

  const saveCloseHandler = async (e) => {
    const success = await performSave();

    if (success) {
      handleSaveCloseSuccess();
    }
  };

  const saveHandler = async (e) => {
    const success = await performSave();

    if (success) {
      handleSaveSuccess();
    }
  };

  const performSave = async () => {
    setLoading(true);
    const obj = await createData();

    if (obj.length !== accData.length) {
      setLoading(false);
      return false;
    }

    const response = await saveAccData(obj);
    setLoading(false);

    if (response.message !== "Success") {
      setSnack({
        open: true,
        message: "Failed to apply changes.",
        severity: "error",
      });
      return false;
    }

    return true;
  };

  const handleSaveCloseSuccess = () => {
    const obj = {
      open: true,
      message: "Update completed successfully.",
      severity: "success",
    };
    dispatch(bulkEditStoreAction.setPreviousTabName(""));
    dispatch(SnackBarStoreAction.updateSnack(obj));
    navigateBack();
  };

  const handleSaveSuccess = () => {
    setSnack({
      open: true,
      message: "Update completed successfully.",
      severity: "success",
    });
    fetchData();
    setAgentComments("");
    setGlobalDate("");
  };

  const createData = async () => {
    const obj = [];
    for (const element of accData) {
      const validation = validateElement(element);

      if (!validation.isValid) {
        setSnack({
          open: true,
          message: validation.message,
          severity: "error",
        });
        break;
      }

      obj.push(buildSaveObject(element));
    }
    return obj;
  };

  const validateElement = (element) => {
    if (element.date === "") {
      return {
        isValid: false,
        message: `Invalid date at Oracle Id : ${element.OracleId}`,
      };
    }

    if (
      element.date !== null &&
      element.date !== "YYYY-MM-DDTHH:mm" &&
      moment(new Date(element.date)).format("YYYY-MM-DD") <= MINIMUM_DATE
    ) {
      return {
        isValid: false,
        message: `The date must be later than the minimum specified date ${MINIMUM_DATE} at Oracle Id : ${element.OracleId}`,
      };
    }

    return { isValid: true };
  };

  const buildSaveObject = (element) => {
    return {
      agentComments: element.AgentComments,
      classTraineeId: element.ClassTraineeId,
      accDate:
        element.date === null || element.date === "YYYY-MM-DDTHH:mm"
          ? null
          : moment(new Date(element.date)).format("YYYY-MM-DD"),
      accTime:
        element.date === null || element.date === "YYYY-MM-DDTHH:mm"
          ? null
          : moment(new Date(element.date)).format("HH:mm"),
      modifiedBy: localStorage.getItem("User"),
    };
  };

  const navigateBack = () => {
    navigate(
      `/home/searchrosters?classRequestId=${classRequestId}&classRosterId=${classRosterId}`,
    );
  };

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <TableContainer className={classes.container} component={Paper}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell
                className={"text-center align-center"}
                align="center"
                sx={{
                  fontFamily: "Urbanist",
                  fontWeight: "bold",
                  fontSize: "16px",
                  lineHeight: "24px",
                  letterSpacing: "0%",
                  backgroundColor: "#e5e4e2",
                  color: "#3F5765",
                  minWidth: "300px !important",
                  position: "sticky",
                  left: 0,
                  top: "0 !important",
                  zIndex: "1100 !important",
                  boxShadow: "2px 0 5px -2px rgba(0,0,0,0.15)",
                }}
                onClick={() => sorting("Name")}
              >
                <span>{t("name")}</span>
                <Box sx={{ display: "inline-block" }}>
                  <ArrowUpwardIcon                    
                    sx={{
                      fontSize: 16,
                      color: order === "ASC" ? "#1976d2" : "#9e9e9e",
                      verticalAlign: "middle",
                    }}
                  />
                  <ArrowDownwardIcon                    
                    sx={{
                      fontSize: 16,
                      color: order === "DSC" ? "#1976d2" : "#9e9e9e",
                      verticalAlign: "middle",
                    }}
                  />
                </Box>
                <Typography
                  className={classes.blueColor}
                  sx={{ fontSize: "14px", margin: "0" }}
                >
                  Total: {totalActiveCount.total} | Active:{" "}
                  {totalActiveCount.active}
                </Typography>
              </TableCell>
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
              >
                <span>{t("ACCDate")}</span>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <TextField
                      id="datetime-local"
                      type="datetime-local"
                      value={globalDate}
                      className={classes.timeInput}
                      sx={{ padding: 0 }}
                      onKeyDown={(e) =>
                        handleAll(
                          e.key === "Backspace" ? "" : e.target.value,
                          "date",
                        )
                      }
                      onChange={(e) => handleAll(e.target.value, "date")}
                      InputLabelProps={{
                        shrink: true,
                        inputFormat: "YYYY-MM-DD : HH:MM",
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </TableCell>
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
                onClick={() => sorting("ACCStatus")}
              >
                <span>{t("ACCStatus")}</span>
                <span>
                  <ArrowUpwardIcon
                    onClick={() => sorting("ACCStatus")}
                    className={`${classes.arrowIcon} ${
                      sortFlag["ACCStatus"] === "ASC"
                        ? classes.arrowAsc
                        : classes.arrowInactive
                    }`}
                  />
                  <ArrowDownwardIcon
                    onClick={() => sorting("ACCStatus")}
                    className={`${classes.arrowIcon} ${
                      sortFlag["ACCStatus"] === "DSC"
                        ? classes.arrowDesc
                        : classes.arrowInactive
                    }`}
                  />
                </span>
              </TableCell>
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
                onClick={() => sorting("ACCSRWDisclaimerSignedDate")}
              >
                <span>{t("SRWDisclaimer")}</span>{" "}
                <span>
                  <ArrowUpwardIcon
                    onClick={() => sorting("ACCSRWDisclaimerSignedDate")}
                    className={`${classes.arrowIcon} ${
                      sortFlag["ACCSRWDisclaimerSignedDate"] === "ASC"
                        ? classes.arrowAsc
                        : classes.arrowInactive
                    }`}
                  />
                  <ArrowDownwardIcon
                    onClick={() => sorting("ACCSRWDisclaimerSignedDate")}
                    className={`${classes.arrowIcon} ${
                      sortFlag["ACCSRWDisclaimerSignedDate"] === "DSC"
                        ? classes.arrowDesc
                        : classes.arrowInactive
                    }`}
                  />
                </span>
              </TableCell>
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
                onClick={() => sorting("ACCAntiVirusStatus")}
              >
                <span>{t("AntiVirus")}</span>
                <span>
                  <ArrowUpwardIcon
                    onClick={() => sorting("ACCAntiVirusStatus")}
                    className={`${classes.arrowIcon} ${
                      sortFlag["ACCAntiVirusStatus"] === "ASC"
                        ? classes.arrowAsc
                        : classes.arrowInactive
                    }`}
                  />
                  <ArrowDownwardIcon
                    onClick={() => sorting("ACCAntiVirusStatus")}
                    className={`${classes.arrowIcon} ${
                      sortFlag["ACCAntiVirusStatus"] === "DSC"
                        ? classes.arrowDesc
                        : classes.arrowInactive
                    }`}
                  />
                </span>
              </TableCell>
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
                onClick={() => sorting("ACCAudioStatus")}
              >
                <span> {t("Audio")}</span>
                <span>
                  <ArrowUpwardIcon
                    onClick={() => sorting("ACCAudioStatus")}
                    className={`${classes.arrowIcon} ${
                      sortFlag["ACCAudioStatus"] === "ASC"
                        ? classes.arrowAsc
                        : classes.arrowInactive
                    }`}
                  />
                  <ArrowDownwardIcon
                    onClick={() => sorting("ACCAudioStatus")}
                    sx={{ fontSize: 14, color: "#C90370" }}
                    className={`${classes.arrowIcon} ${
                      sortFlag["ACCAudioStatus"] === "DSC"
                        ? classes.arrowDesc
                        : classes.arrowInactive
                    }`}
                  />
                </span>
              </TableCell>
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
                onClick={() => sorting("ACCFirewallStatus")}
              >
                <span> {t("Firewall")}</span>
                <span>
                  <ArrowUpwardIcon
                    onClick={() => sorting("ACCFirewallStatus")}
                    className={`${classes.arrowIcon} ${
                      sortFlag["ACCFirewallStatus"] === "ASC"
                        ? classes.arrowAsc
                        : classes.arrowInactive
                    }`}
                  />
                  <ArrowDownwardIcon
                    onClick={() => sorting("ACCFirewallStatus")}
                    className={`${classes.arrowIcon} ${
                      sortFlag["ACCFirewallStatus"] === "DSC"
                        ? classes.arrowDesc
                        : classes.arrowInactive
                    }`}
                  />
                </span>
              </TableCell>
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
                onClick={() => sorting("ACCNetworkStatus")}
              >
                <span> {t("Network")}</span>
                <span>
                  <ArrowUpwardIcon
                    onClick={() => sorting("ACCNetworkStatus")}
                    className={`${classes.arrowIcon} ${
                      sortFlag["ACCNetworkStatus"] === "ASC"
                        ? classes.arrowAsc
                        : classes.arrowInactive
                    }`}
                  />
                  <ArrowDownwardIcon
                    onClick={() => sorting("ACCNetworkStatus")}
                    className={`${classes.arrowIcon} ${
                      sortFlag["ACCNetworkStatus"] === "DSC"
                        ? classes.arrowDesc
                        : classes.arrowInactive
                    }`}
                  />
                </span>
              </TableCell>
              <TableCell
                className={`${classes.tableHeaderCell} text-center align-center`}
                onClick={() => sorting("ACCWindowsUpdateStatus")}
              >
                <span>{t("WinUpdate")}</span>
                <span>
                  <ArrowUpwardIcon
                    onClick={() => sorting("ACCWindowsUpdateStatus")}
                    className={`${classes.arrowIcon} ${
                      sortFlag["ACCWindowsUpdateStatus"] === "ASC"
                        ? classes.arrowAsc
                        : classes.arrowInactive
                    }`}
                  />
                  <ArrowDownwardIcon
                    onClick={() => sorting("ACCWindowsUpdateStatus")}
                    className={`${classes.arrowIcon} ${
                      sortFlag["ACCWindowsUpdateStatus"] === "DSC"
                        ? classes.arrowDesc
                        : classes.arrowInactive
                    }`}
                  />
                </span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accData.length > 0 &&
              accData.map((row, i) => {
                const rowKey = `${row.OracleId}-${i}`;
                return (
                  <TableRow key={rowKey}>
                    <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: "#fff",
                        zIndex: 1,
                        boxShadow: "2px 0 5px -2px rgba(0,0,0,0.15)",
                      }}
                    >
                      <Grid
                        container
                        direction="row"
                        justifyContent="center"
                        columns={1}
                        alignItems="center"
                        spacing={0}
                      >
                        {renderTextCell(
                          `${row.Name}(${row.OracleId})`,
                          row.TraineeStatus,
                          row.TraineeStatus !== "Active",
                        )} 
                        {row.TTECRegistrationStatus ? (
                          <Grid item xs={0.1}>
                            <Tooltip title="TTEConnect: Registered">
                              <PowerSharpIcon
                                placeholder="TTEConnect: Registered"
                                sx={{ color: "#69DE00" }}
                              />
                            </Tooltip>
                          </Grid>
                        ) : (
                          <Grid item xs={0.1}></Grid>
                        )}
                        <LastUpdateTooltip
                          AgentCommentsModifiedDate={
                            row.AgentCommentsModifiedDate
                          }
                          AgentCommentsModifiedBy={row.AgentCommentsModifiedBy}
                        >
                          <textarea
                            className={classes.agentCommentInputContainer}
                            value={row.AgentComments ? row.AgentComments : ""}
                            maxLength={4000}
                            placeholder="Max 4000 characters allowed"
                            onChange={(e) =>
                              handleIndividualChange(
                                e.target.value,
                                row,
                                "AgentComments",
                              )
                            }
                            style={{ width: "100%", minWidth: "350px" }}
                          ></textarea>
                        </LastUpdateTooltip>
                      </Grid>
                    </TableCell>
                    <TableCell align="center" sx={{ padding: 0 }}>
                      <TextField
                        id="datetime-local"
                        type="datetime-local"
                        className={classes.timeInput}
                        value={row.date}
                        onChange={(e) =>
                          handleIndividualChange(
                            e.target.value === ""
                              ? "YYYY-MM-DDTHH:mm"
                              : e.target.value,
                            row,
                            "date",
                          )
                        }
                        onKeyDown={(e) =>
                          handleIndividualChange(
                            e.key === "Backspace"
                              ? "YYYY-MM-DDTHH:mm"
                              : e.target.value,
                            row,
                            "date",
                          )
                        }
                        InputLabelProps={{
                          shrink: true,
                          inputFormat: "YYYY-MM-DD : HH:MM",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <span> {row.ACCStatus}</span>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {row.ACCSRWDisclaimerSignedDate ? "Signed:" : null}
                      {row.SRWdate}
                      <br />
                      {row.SRWtime}
                    </TableCell>
                    <TableCell align="center">
                      <span> {row.ACCAntiVirusStatus}</span>
                    </TableCell>
                    <TableCell align="center">
                      <span> {row.ACCAudioStatus}</span>
                    </TableCell>
                    <TableCell align="center">
                      <span> {row.ACCFirewallStatus}</span>
                    </TableCell>
                    <TableCell align="center">
                      <span>{row.ACCNetworkStatus}</span>
                    </TableCell>
                    <TableCell align="center">
                      <span> {row.ACCWindowsUpdateStatus}</span>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          paddingTop: 2,
          gap: 1,
        }}
      >
        <CustomOutlinedButton variant="outlined" onClick={navigateBack}>
          {t("CANCEL")}
        </CustomOutlinedButton>
        <CustomButton onClick={(e) => saveHandler(e)}>{t("SAVE")}</CustomButton>
        <CustomButton onClick={(e) => saveCloseHandler(e)}>
          {t("SAVE+CLOSE")}
        </CustomButton>
      </Grid>
    </>
  );
};
ACCV2.prototype = {
  filterStatus: PropTypes.string,
  roster: PropTypes.string,
  closeLoader: PropTypes.func,
  resetStatus: PropTypes.bool,
  onChange: PropTypes.func,
};

export default ACCV2;
